/**
 * CONTROLE DE CAMPO — AVANÇO DAS INSPEÇÕES POR LINHA.
 *
 * Regras puras (sem serviço, sem repositório, sem UI) do acompanhamento de
 * campo de cada Linha de Tubulação:
 *
 *   ATRIBUIÇÃO   Inspetor → Sistema de Tubulação (`FieldInspectionAssignment`)
 *   AVANÇO       Linha → 0..100% (`FieldInspectionProgress`)
 *
 * O avanço pertence à LINHA, não ao Inspetor: dois Inspetores vinculados ao
 * mesmo Sistema enxergam e alteram o MESMO número — só muda quem fez a
 * última alteração (comando, item 8). Por isso a chave natural do avanço é
 * `lineEquipmentId`, nunca a dupla Linha+Inspetor.
 *
 * Status nunca é digitado: é derivado do percentual e da condição
 * operacional (itens 11/16).
 */

/** Status derivado — nunca digitado pelo usuário (comando, item 11). */
export const FieldInspectionStatus = {
  NotStarted: "NÃO INICIADA",
  InProgress: "EM ANDAMENTO",
  Completed: "CONCLUÍDA",
  OutOfOperation: "FORA DE OPERAÇÃO"
} as const;
export type FieldInspectionStatus = (typeof FieldInspectionStatus)[keyof typeof FieldInspectionStatus];

/**
 * Avanço de campo de UMA Linha. O contexto (Cliente/OS/Projeto/Unidade) é
 * gravado por ID real (item 6) para o Painel poder filtrar sem reabrir o
 * cadastro de cada Linha.
 */
export interface FieldInspectionProgress {
  id: string;
  clientId: string;
  serviceOrderId: string | null;
  projectId: string | null;
  unitAreaId: string | null;
  /** Equipment.id do SISTEMA DE TUBULAÇÃO; vazio para Linha NOT_APPLICABLE sem Sistema. */
  systemId: string;
  /** Alvo canônico do avanço: Sistema legado ou Linha direta. */
  targetType: FieldControlTargetType;
  targetId: string;
  /** Equipment.id da LINHA (PIPING_LINE) — chave natural do avanço. */
  lineEquipmentId: string;
  /** 0..100, inteiro. */
  progressPercent: number;
  startedAt: string | null;
  completedAt: string | null;
  /** Fora de Operação é condição SEPARADA do percentual (item 16). */
  outOfOperation: boolean;
  outOfOperationReason: string | null;
  /** Registro operacional livre — NÃO substitui Recommendation (item 21). */
  fieldNote: string | null;
  updatedBy: string | null;
  updatedAt: string;
  /** Concorrência otimista: quem gravar com versão velha é recusado (item 41). */
  version: number;
}

/** Vínculo Inspetor → Sistema ou Linha (comando P3). */
export type FieldControlTargetType = "SYSTEM" | "LINE";

export interface FieldInspectionAssignment {
  id: string;
  inspectorProfessionalId: string;
  clientId: string;
  serviceOrderId: string | null;
  projectId: string | null;
  unitAreaId: string | null;
  /** Legado: preenchido para targetType=SYSTEM; null para targetType=LINE. */
  systemId: string | null;
  targetType: FieldControlTargetType;
  targetId: string;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
}

/** Erro de domínio do avanço — o serviço traduz para AppError. */
export class FieldProgressRuleError extends Error {}

/**
 * Percentual válido: inteiro de 0 a 100. Recusa NaN, texto, negativo e
 * acima de 100 (item 10) em vez de "corrigir" silenciosamente — o valor que
 * o usuário digitou errado não vira outro número sem ele saber.
 */
export function normalizeProgressPercent(raw: unknown): number {
  let value: number;
  if (typeof raw === "number") {
    value = raw;
  } else {
    // Vazio/nulo NÃO é zero: `Number("")` devolve 0 e transformaria "campo em
    // branco" num apontamento real de 0% que o usuário nunca fez.
    const text = String(raw ?? "").trim().replace(",", ".");
    if (text === "") throw new FieldProgressRuleError("Avanço inválido: informe um número de 0 a 100.");
    value = Number(text);
  }
  if (!Number.isFinite(value)) throw new FieldProgressRuleError("Avanço inválido: informe um número de 0 a 100.");
  if (value < 0) throw new FieldProgressRuleError("Avanço não pode ser negativo.");
  if (value > 100) throw new FieldProgressRuleError("Avanço não pode ser maior que 100%.");
  return Math.round(value);
}

/**
 * Status derivado (itens 11/16). FORA DE OPERAÇÃO prevalece sobre o
 * percentual: a Linha não está concluída nem pendente ativa — é uma terceira
 * categoria, contada à parte.
 */
export function statusOf(progress: Pick<FieldInspectionProgress, "progressPercent" | "outOfOperation">): FieldInspectionStatus {
  if (progress.outOfOperation) return FieldInspectionStatus.OutOfOperation;
  if (progress.progressPercent >= 100) return FieldInspectionStatus.Completed;
  if (progress.progressPercent > 0) return FieldInspectionStatus.InProgress;
  return FieldInspectionStatus.NotStarted;
}

/** Linha operacional: a que entra no cálculo de avanço (itens 29/30). */
export function isOperational(progress: Pick<FieldInspectionProgress, "outOfOperation">): boolean {
  return !progress.outOfOperation;
}

/** CONCLUÍDA = 100% E operacional (item 31). */
export function isCompleted(progress: Pick<FieldInspectionProgress, "progressPercent" | "outOfOperation">): boolean {
  return isOperational(progress) && progress.progressPercent >= 100;
}

/** PENDENTE = operacional com 0..99% (item 32). */
export function isPending(progress: Pick<FieldInspectionProgress, "progressPercent" | "outOfOperation">): boolean {
  return isOperational(progress) && progress.progressPercent < 100;
}

export interface FieldProgressSummary {
  /** Todas as Linhas no escopo, inclusive as Fora de Operação. */
  total: number;
  notStarted: number;
  inProgress: number;
  completed: number;
  outOfOperation: number;
  /** Linhas que entram no denominador do avanço (item 30). */
  operational: number;
  /**
   * Média dos percentuais das Linhas OPERACIONAIS (item 29). Sem Linha
   * operacional, é 0 — nunca NaN por divisão por zero.
   */
  realProgressPercent: number;
}

/**
 * Consolidação oficial do avanço (itens 28-32). Uma única implementação:
 * a página de Controle de Campo e o Painel de Indicadores consomem esta
 * mesma função, para não existirem duas contas diferentes do mesmo número.
 */
export function summarizeFieldProgress(items: readonly FieldInspectionProgress[]): FieldProgressSummary {
  const operationalItems = items.filter(isOperational);
  const sum = operationalItems.reduce((total, item) => total + item.progressPercent, 0);
  return {
    total: items.length,
    notStarted: items.filter(item => statusOf(item) === FieldInspectionStatus.NotStarted).length,
    inProgress: items.filter(item => statusOf(item) === FieldInspectionStatus.InProgress).length,
    completed: items.filter(isCompleted).length,
    outOfOperation: items.filter(item => item.outOfOperation).length,
    operational: operationalItems.length,
    realProgressPercent: operationalItems.length === 0 ? 0 : sum / operationalItems.length
  };
}

/** Agrupa e resume por uma chave qualquer — usado por Sistema, Unidade e Inspetor (itens 36/37/39). */
export function summarizeGroupedBy<K extends string>(
  items: readonly FieldInspectionProgress[],
  keyOf: (item: FieldInspectionProgress) => K | null
): Map<K, FieldProgressSummary> {
  const groups = new Map<K, FieldInspectionProgress[]>();
  for (const item of items) {
    const key = keyOf(item);
    if (key === null) continue;
    const bucket = groups.get(key);
    if (bucket) bucket.push(item);
    else groups.set(key, [item]);
  }
  const out = new Map<K, FieldProgressSummary>();
  for (const [key, bucket] of groups) out.set(key, summarizeFieldProgress(bucket));
  return out;
}

/** O que o usuário pediu para mudar numa Linha. Campo ausente = não mexer. */
export interface FieldProgressChange {
  progressPercent?: unknown;
  startedAt?: string | null;
  completedAt?: string | null;
  outOfOperation?: boolean;
  outOfOperationReason?: string | null;
  fieldNote?: string | null;
  /** Obrigatório para reduzir uma Linha já concluída (item 15). */
  reason?: string | null;
}

/**
 * Máquina de estado do avanço (itens 12-15, 18, 19). Pura: recebe o estado
 * atual e a intenção, devolve o próximo estado — sem relógio global, sem
 * repositório, sem usuário logado implícito.
 *
 * Ordem das regras, porque elas se cruzam:
 *  1. Fora de Operação é tratado primeiro: marcar exige motivo (item 17) e
 *     desmarcar devolve a Linha ao percentual que ela já tinha (item 18) —
 *     em nenhum dos dois casos o percentual é alterado por conta própria.
 *  2. DATA DE TÉRMINO informada força 100%/CONCLUÍDA (item 13).
 *  3. 100% sem data de término preenche a data com "agora" (item 14).
 *  4. Sair de 0% preenche a DATA DE INÍCIO se estiver vazia (item 12).
 *  5. Reduzir uma Linha de 100% exige motivo e limpa a data de término
 *     (item 15).
 */
export function applyProgressChange(
  current: FieldInspectionProgress,
  change: FieldProgressChange,
  now: string,
  actorUserId: string | null
): FieldInspectionProgress {
  const next: FieldInspectionProgress = { ...current };

  if (change.outOfOperation !== undefined && change.outOfOperation !== current.outOfOperation) {
    if (change.outOfOperation) {
      const reason = (change.outOfOperationReason ?? change.reason ?? "").trim();
      if (reason === "") {
        throw new FieldProgressRuleError("Informe o motivo para marcar a Linha como FORA DE OPERAÇÃO.");
      }
      next.outOfOperation = true;
      next.outOfOperationReason = reason;
    } else {
      // Retorno à operação: mantém o percentual anterior e NÃO conclui nada
      // sozinho (item 18).
      next.outOfOperation = false;
      next.outOfOperationReason = null;
    }
  }

  if (change.progressPercent !== undefined) {
    const requested = normalizeProgressPercent(change.progressPercent);
    if (current.progressPercent >= 100 && requested < 100) {
      const reason = (change.reason ?? "").trim();
      if (reason === "") {
        throw new FieldProgressRuleError("Reduzir o avanço de uma Linha CONCLUÍDA exige um motivo.");
      }
      next.completedAt = null;
    }
    next.progressPercent = requested;
  }

  if (change.startedAt !== undefined) next.startedAt = change.startedAt;

  if (change.completedAt !== undefined) {
    next.completedAt = change.completedAt;
    // Data de término informada = inspeção concluída (item 13).
    if (change.completedAt) next.progressPercent = 100;
  }

  // 100% sem data de término: a data é a de hoje (item 14).
  if (next.progressPercent >= 100 && !next.completedAt) next.completedAt = now;
  // Saiu do zero: registra o início se ainda não houver (item 12).
  if (next.progressPercent > 0 && !next.startedAt) next.startedAt = now;

  if (change.fieldNote !== undefined) next.fieldNote = change.fieldNote;

  next.updatedBy = actorUserId;
  next.updatedAt = now;
  next.version = current.version + 1;
  return next;
}

/** Recorte de consulta — sempre por ID real, nunca por nome/TAG (item 6). */
export interface FieldProgressFilter {
  clientId?: string | undefined;
  projectId?: string | undefined;
  unitAreaId?: string | undefined;
  systemIds?: readonly string[] | undefined;
  lineEquipmentIds?: readonly string[] | undefined;
}

/** Recorte dos vínculos Inspetor → Sistema ou Linha. */
export interface FieldAssignmentFilter {
  clientId?: string | undefined;
  inspectorProfessionalId?: string | undefined;
  systemId?: string | undefined;
  targetType?: FieldControlTargetType | undefined;
  targetId?: string | undefined;
  activeOnly?: boolean | undefined;
}

/**
 * Porta de persistência do Controle de Campo. Uma implementação em memória
 * (sessão local) e uma em SQLite (servidor central, multiusuário) — a regra
 * de negócio não sabe qual está em uso.
 */
export interface FieldControlRepository {
  /** Avanço de uma Linha. Um único registro por Linha (item 8). */
  findProgressByLine(lineEquipmentId: string): Promise<FieldInspectionProgress | null>;
  listProgress(filter: FieldProgressFilter): Promise<FieldInspectionProgress[]>;
  /**
   * Grava respeitando concorrência otimista: `expectedVersion` precisa bater
   * com a versão gravada, senão a operação é recusada (item 41). Para um
   * registro novo, `expectedVersion` é 0.
   */
  saveProgress(progress: FieldInspectionProgress, expectedVersion: number): Promise<void>;

  listAssignments(filter: FieldAssignmentFilter): Promise<FieldInspectionAssignment[]>;
  saveAssignment(assignment: FieldInspectionAssignment): Promise<void>;
  findAssignmentById(id: string): Promise<FieldInspectionAssignment | null>;
}

/** Estado inicial de uma Linha que ainda não teve apontamento de campo. */
export function newFieldInspectionProgress(seed: {
  id: string;
  clientId: string;
  serviceOrderId?: string | null;
  projectId?: string | null;
  unitAreaId?: string | null;
  /** Sistema pai; vazio quando a Linha é NOT_APPLICABLE e tem alvo direto. */
  systemId: string | null;
  targetType?: FieldControlTargetType;
  targetId?: string;
  lineEquipmentId: string;
  now: string;
}): FieldInspectionProgress {
  return {
    id: seed.id,
    clientId: seed.clientId,
    serviceOrderId: seed.serviceOrderId ?? null,
    projectId: seed.projectId ?? null,
    unitAreaId: seed.unitAreaId ?? null,
    systemId: seed.systemId ?? "",
    targetType: seed.targetType ?? (seed.systemId ? "SYSTEM" : "LINE"),
    targetId: seed.targetId ?? (seed.systemId || seed.lineEquipmentId),
    lineEquipmentId: seed.lineEquipmentId,
    progressPercent: 0,
    startedAt: null,
    completedAt: null,
    outOfOperation: false,
    outOfOperationReason: null,
    fieldNote: null,
    updatedBy: null,
    updatedAt: seed.now,
    version: 1
  };
}
